/**
 * PersistenceServiceModule — Engine E1e (2026-06-01).
 *
 * Wraps the briefing-room backend's campaigns REST API. M3a shipped this
 * as a STUB throwing ServiceUnavailableError; E1c shipped the routes at
 * a7759c3b9; this E1e swap implements the live wrapper.
 *
 * Routes (per backend/routes/campaigns.py):
 *   GET    /api/modules/briefing-room/campaigns        → CampaignMeta[]
 *   GET    /api/modules/briefing-room/campaigns/{id}   → CampaignMeta | 404
 *   POST   /api/modules/briefing-room/campaigns        → CampaignMeta (201)
 *
 * Auth: Bearer JWT from localStorage (TOKEN_KEY shared with vibe-designer).
 * Constructor accepts an optional fetchFn for testability — vitest mocks
 * use this pattern across the host services suite.
 *
 * CampaignHandle.close() is a no-op resolving to void: the backend has
 * no per-campaign connection lifecycle (each request opens + disposes
 * the SQLAlchemy engine), so there's nothing to release client-side.
 */
import type {
  CampaignHandle,
  CampaignMeta,
  PersistenceService,
} from '../types';
import { getAuthHeaders } from './authHeaders';

const CAMPAIGNS_PATH = '/api/modules/briefing-room/campaigns';

export class PersistenceServiceModule implements PersistenceService {
  constructor(
    private readonly basePath: string = CAMPAIGNS_PATH,
    // Wrap fetch in an arrow so the default doesn't capture a bare reference.
    // Calling `this.fetchFn(...)` on a bare-reference default throws "Illegal
    // invocation" in real browsers because fetch requires `this === window`.
    // The arrow re-routes the call as a normal global fetch invocation.
    // Tests inject a vi.fn() mock so the default is only exercised at runtime.
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  isAvailable(): boolean {
    return true;
  }

  async listCampaigns(): Promise<CampaignMeta[]> {
    const resp = await this.fetchFn(this.basePath, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `PersistenceServiceModule: listCampaigns HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as CampaignMeta[];
  }

  async openCampaign(id: string): Promise<CampaignHandle> {
    const url = `${this.basePath}/${encodeURIComponent(id)}`;
    const resp = await this.fetchFn(url, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `PersistenceServiceModule: openCampaign HTTP ${resp.status}`,
      );
    }
    const meta = (await resp.json()) as CampaignMeta;
    return {
      meta,
      close: async () => {
        /* no-op — backend has no per-campaign connection lifecycle */
      },
    };
  }

  async createCampaign(name: string, moduleId: string): Promise<CampaignMeta> {
    const resp = await this.fetchFn(this.basePath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ name, module_id: moduleId }),
    });
    if (!resp.ok) {
      throw new Error(
        `PersistenceServiceModule: createCampaign HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as CampaignMeta;
  }

  async deleteCampaign(id: string): Promise<void> {
    const url = `${this.basePath}/${encodeURIComponent(id)}`;
    const resp = await this.fetchFn(url, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `PersistenceServiceModule: deleteCampaign HTTP ${resp.status}`,
      );
    }
  }
}
